import { SubscriptionType } from '@/@types/enums/SubscriptionType';
import { GUIDES_APP } from '@/helpers/appContext';

const ACTIVE_SUBSCRIPTION_STATUSES: (SubscriptionType | null | undefined)[] = [
  SubscriptionType.ACTIVE,
  SubscriptionType.TRIALING,
  SubscriptionType.PAST_DUE,
];

/**
 * C7: which product the Plans page should show prices for.
 *
 * On a fresh subscribe visit, the query/session guides context (set by the
 * auth pages) decides, and a company whose last subscription was a Guides one
 * stays on Guides: holding kb as well, it would otherwise see Snaplore prices
 * from the not-paid page, and paying there would end Guides. On a CHANGE-PLAN visit, that context cannot
 * be trusted — a fresh tab has empty sessionStorage, and nothing threads the
 * query through reliably — so it must derive from the company's ACTUAL
 * current subscription tag (`paddle.app`) instead.
 */
export const resolvePlansPageIsGuides = (params: {
  isUpdatePlan: boolean;
  companyPaddleApp?: string | null;
  queryIsGuides: boolean;
  /** SNAP-2231: a Guides-only company subscribes to Guides with or without ?app=guides. */
  companyIsGuidesOnly?: boolean;
}): boolean =>
  params.isUpdatePlan
    ? params.companyPaddleApp === GUIDES_APP
    : params.queryIsGuides || !!params.companyIsGuidesOnly || params.companyPaddleApp === GUIDES_APP;

/**
 * D2: a company already paying (active/trialing/past_due) for the OTHER
 * product must see a non-checkout notice instead of subscribe buttons — never
 * on a change-plan visit, where the page IS for the current product.
 */
export const hasOtherProductSubscription = (params: {
  isUpdatePlan: boolean;
  paddleStatus?: SubscriptionType | null;
  paddleApp?: string | null;
  isGuides: boolean;
}): boolean => {
  if (params.isUpdatePlan) return false;
  if (!ACTIVE_SUBSCRIPTION_STATUSES.includes(params.paddleStatus)) return false;
  return params.isGuides ? params.paddleApp !== GUIDES_APP : params.paddleApp === GUIDES_APP;
};

/**
 * The company's current subscription among all of its owner's. A company that bought again after a pause has both
 * at Paddle, and the API refuses to change or pause the old one.
 */
export const findCompanySubscription = <T extends { id: string; customData?: { companyId?: string } | null }>(
  subscriptions: T[],
  company?: { id?: string; paddle?: { subscriptionId?: string | null } | null } | null
): T | undefined =>
  subscriptions.find((subscription) => subscription.id === company?.paddle?.subscriptionId) ??
  subscriptions.find((subscription) => subscription.customData?.companyId === company?.id);
